import { Box } from '@mui/material'
import MiniChart from '@/components/Table/MiniChart'
import CrmStatus from '@/components/Table/CrmStatus'
import CellWithCopy from '@/components/Table/CellWithCopy'
import { HEBREW_HEADERS, ConsumerRow } from '@/services/consumerService'
import MeterLink from '@/components/Table/MeterLink'
import {
  VALUE_NAMES_FIELD,
  formatRowCode,
  renderCodeCell,
  valueNameOf,
} from '@/utils/codeName'

// Desired column order — fields listed here appear first (in this order).
// Any field not listed will appear after these, in the order returned by the API.
export const COLUMN_ORDER: string[] = [
  'meterNumber',
  'meterModelName',
  'lastConsumption',
  'lastValue',
  'lastReadingAt',
  'payerName',
  'dailyUsageData',
  'avgWeeklyConsumption',
  'billingId',
  'address',
  'rValue',
  'date',
  'diameter',
  'occupantCount',
  'locationId',
  'parentCard',
  'isSiteMain',
  'payerId',
  'phoneNumber',
  'emails',
  'email',
  'usageType',
  'amrIsActive',
  'amrSystemKey',
  'installedPeriod',
  'crm',
]

// Check if a value is meaningful (non-empty)
export const isMeaningful = (value: any): boolean => {
  if (value === null || value === undefined) return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'string') return value.trim() !== ''
  if (typeof value === 'number') return true
  if (typeof value === 'boolean') return true
  if (typeof value === 'object') return Object.keys(value).length > 0
  return false
}

// Format reading value: up to 3 decimal places, drop trailing zeros (e.g. 12.100 -> 12.1)
export const formatReadingValue = (value: any): string => {
  if (value == null || value === '') return '-'
  const num = Number(value)
  if (isNaN(num)) return String(value)
  return parseFloat(num.toFixed(3)).toString()
}

// The night's day and hours ride on the row to label the night cell. They are not columns.
const NIGHT_DETAIL_FIELDS = [
  'nightDay',
  'nightPeriodFrom',
  'nightPeriodTo',
] as const

/**
 * Build dynamic consumer columns from API data.
 * Used by both Consumers and Map pages.
 */
export function buildConsumerColumns({
  rows,
  resourceNames,
  resourceHeaders,
  langKey,
  arrivalDateFilter = false,
}: {
  rows: ConsumerRow[]
  resourceNames: string[]
  resourceHeaders: Record<string, string>
  langKey: string
  /** Staff only: give the arrival date column a date range filter. */
  arrivalDateFilter?: boolean
}) {
  // Ensure lastValue/lastReadingAt are in the field list even if the API omits null keys
  const allFieldNames = [...resourceNames]
  for (const f of ['lastValue', 'lastReadingAt', 'avgWeeklyConsumption', 'lastConsumption']) {
    if (!allFieldNames.includes(f)) allFieldNames.push(f)
  }

  // 1. Filter out empty columns — only keep fields that have at least one meaningful value
  //    When rows is empty (e.g. no search results), skip filtering to preserve all columns.
  let available: string[]
  if (rows.length === 0) {
    available = allFieldNames
  } else {
    const nonEmptyFields = new Set<string>()
    for (const row of rows) {
      for (const [key, value] of Object.entries(row)) {
        if (key !== 'id' && isMeaningful(value)) {
          nonEmptyFields.add(key)
        }
      }
    }
    // dailyUsageData is always shown if present in resourceNames (chart column)
    if (resourceNames.includes('dailyUsageData')) {
      nonEmptyFields.add('dailyUsageData')
    }

    // Always show these columns even if all values are empty
    nonEmptyFields.add('lastValue')
    nonEmptyFields.add('lastReadingAt')
    nonEmptyFields.add('lastConsumption')
    nonEmptyFields.add('meterModelName')

    available = allFieldNames.filter(n => nonEmptyFields.has(n))
  }

  // 2. Sort: ordered fields first, then the rest in their original API order
  const orderIndex = new Map(COLUMN_ORDER.map((f, i) => [f, i]))
  const sorted = [...available].sort((a, b) => {
    const ai = orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER
    const bi = orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER
    if (ai !== bi) return ai - bi
    return available.indexOf(a) - available.indexOf(b)
  })

  // Exclude unwanted duplicate columns like premiseId
  const explicitExcludes = new Set([
    ...NIGHT_DETAIL_FIELDS,
    'premiseId',
    'premise_id',
    'amrId',
    'amr_id',
    VALUE_NAMES_FIELD,
  ])
  const finalSorted = sorted.filter(name => !explicitExcludes.has(name))

  // A column whose rows carry the names of its codes shows each code with its name.
  const namedFields = new Set(
    finalSorted.filter(name => rows.some(row => valueNameOf(row, name)))
  )

  // 3. Build column definitions
  return finalSorted.map(name => {
    // The Gateway names some resources with their own key; that is no label.
    const gatewayHeader = resourceHeaders[name] !== name ? resourceHeaders[name] : undefined
    const displayName = gatewayHeader ?? (langKey === 'he' ? HEBREW_HEADERS[name] : undefined) ?? name
    const textLen = (displayName || '').trim().length
    // Header title (~9.5px/char) + filter icon (24px) + sort icon (20px) + column padding (16px) + buffer (15px)
    const headerTitleNeededWidth = Math.max(140, Math.ceil(textLen * 9.5) + 75)

    const base: any = {
      field: name,
      headerName: displayName,
      flex: 1,
      minWidth: headerTitleNeededWidth,
      hideable: true,
      filter: { type: 'search' as const },
      filterable: true,
    }

    // --- Special renderers for known fields ---

    // A named code keeps the code as its value, so the grid filters and sorts by the code.
    if (namedFields.has(name)) base.renderCell = renderCodeCell

    // meterNumber → clickable link to details page
    if (name === 'meterNumber' || name === 'meter_number') {
      base.minWidth = 150
      base.renderCell = (params: any) =>
        params.value != null ? <MeterLink meter={params.value} /> : '-'
    }

    // dailyUsageData → mini chart
    if (name === 'dailyUsageData') {
      base.minWidth = Math.max(160, headerTitleNeededWidth)
      base.filter = { type: 'none' }
      base.filterable = false
      base.sortable = false
      base.disableAggregation = true
      base.renderCell = (params: any) =>
        params.value != null ? (
          <Box sx={{ width: '100%', height: '90%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
            <MiniChart data={params.value} />
          </Box>
        ) : '-'
    }

    // avgWeeklyConsumption → numeric, computed client-side from JSON (not a DB column)
    if (name === 'avgWeeklyConsumption') {
      base.type = 'number'
      base.sortable = false
      base.minWidth = Math.max(180, headerTitleNeededWidth)
      base.filter = { type: 'aggregated' }
      base.renderCell = (params: any) =>
        params.value != null ? formatReadingValue(params.value) : '-'
    }

    // crm → CrmStatus component
    if (name === 'crm') {
      base.filter = { type: 'options' }
      base.disableAggregation = true
      base.renderCell = (params: any) => (params.value ? <CrmStatus value={params.value} /> : '-')
    }

    // Boolean fields → כן / לא
    if (name === 'isSiteMain' || name === 'is_site_main' || name === 'amrIsActive' || name === 'amr_is_active') {
      base.filter = { type: 'options' }
      base.disableAggregation = true
      base.renderCell = (params: any) => (params.value ? 'כן' : 'לא')
    }

    // Options-type filter fields
    if (['diameter', 'locationId', 'location_id', 'occupantCount', 'occupant_count', 'usageType', 'usage_type', 'meterModelName', 'meter_model_name', 'amrSystemKey', 'amr_system_key'].includes(name)) {
      base.filter = { type: 'options' }
    }

    // Date field
    if (name === 'date') {
      base.minWidth = Math.max(180, headerTitleNeededWidth)
      base.filter = { type: 'none' }
      base.disableAggregation = true
      base.sortComparator = (v1: string, v2: string) => {
        const parseDate = (val: string) => {
          if (!val) return 0
          const [year, month, day] = val.split('T')[0].split('-').map(Number)
          return new Date(year, month - 1, day).getTime()
        }
        return parseDate(v1) - parseDate(v2)
      }
    }

    // lastConsumption → numeric (last consumption / reverse)
    if (name === 'lastConsumption' || name === 'last_consumption') {
      base.type = 'number'
      base.minWidth = Math.max(175, headerTitleNeededWidth)
      base.filter = { type: 'aggregated' }
      base.renderCell = (params: any) => formatReadingValue(params?.value)
    }

    // lastValue → numeric (last meter reading)
    if (name === 'lastValue' || name === 'last_value') {
      base.type = 'number'
      base.minWidth = Math.max(175, headerTitleNeededWidth)
      base.filter = { type: 'aggregated' }
      base.renderCell = (params: any) => formatReadingValue(params?.value)
    }

    // billingId / waterCard → client card
    if (name === 'billingId' || name === 'billing_id' || name === 'waterCard' || name === 'water_card') {
      base.minWidth = Math.max(165, headerTitleNeededWidth)
    }

    // lastReadingAt → date format, sort newest first
    if (name === 'lastReadingAt' || name === 'last_reading_at') {
      base.minWidth = 180
      base.filter = { type: 'none' }
      if (arrivalDateFilter) base.filterSpec = { kind: 'date' }
      base.disableAggregation = true
      base.sortingOrder = ['desc', 'asc'] as any // newest first, then oldest
      base.renderCell = (params: any) => {
        const value = params.value
        if (!value) return '-'
        const d = new Date(value)
        if (isNaN(d.getTime())) return value
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const yy = String(d.getFullYear()).slice(-2)
        const hh = String(d.getHours()).padStart(2, '0')
        const min = String(d.getMinutes()).padStart(2, '0')
        return `${dd}/${mm}/${yy} ${hh}:${min}`
      }
    }

    // rValue → numeric
    if (name === 'rValue' || name === 'r_value') {
      base.type = 'number'
      base.filter = { type: 'aggregated' }
      base.renderCell = (params: any) => formatReadingValue(params?.value)
    }

    // phoneNumber → handle arrays and comma-separated
    if (name === 'phoneNumber' || name === 'phone_number') {
      base.renderCell = (params: any) => {
        const value = params.value
        if (!value) return '-'
        if (Array.isArray(value)) {
          if (value.length === 0) return '-'
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{value.map((p: string, i: number) => (<span key={i}>{p}</span>))}</div>)
        }
        if (typeof value === 'string' && value.includes(',')) {
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{value.split(',').map((p: string, i: number) => (<span key={i}>{p.trim()}</span>))}</div>)
        }
        return typeof value === 'number' ? value.toString() : value || '-'
      }
    }

    // emails / email → handle arrays and comma-separated
    if (name === 'emails' || name === 'email') {
      base.minWidth = 200
      base.renderCell = (params: any) => {
        const value = params.value
        if (!value) return '-'
        if (Array.isArray(value)) {
          if (value.length === 0) return '-'
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{value.map((e: string, i: number) => (<span key={i}>{e}</span>))}</div>)
        }
        if (typeof value === 'string' && value.includes(',')) {
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>{value.split(',').map((e: string, i: number) => (<span key={i}>{e.trim()}</span>))}</div>)
        }
        return value || '-'
      }
    }

    // coordinates → format object as "lat, lng"
    if (name === 'coordinates') {
      base.filter = { type: 'none' }
      base.filterable = false
      base.disableAggregation = true
      base.renderCell = (params: any) => {
        const val = params.value
        if (!val) return '-'
        if (typeof val === 'object' && val.lat != null) {
          return `${val.lat}, ${val.lan ?? val.lng ?? ''}`
        }
        return String(val)
      }
    }

    // itmCoordinates → format object as "x, y"
    if (name === 'itmCoordinates') {
      base.filter = { type: 'none' }
      base.filterable = false
      base.disableAggregation = true
      base.renderCell = (params: any) => {
        const val = params.value
        if (!val) return '-'
        if (typeof val === 'object' && val.x != null) {
          return `${val.x}, ${val.y}`
        }
        return String(val)
      }
    }

    // installedPeriod → format as dd/mm/yy
    if (name === 'installedPeriod' || name === 'installed_period') {
      base.filter = { type: 'none' }
      base.filterable = false
      base.disableAggregation = true
      base.renderCell = (params: any) => {
        const value = params.value
        if (!value) return '-'
        const raw = Array.isArray(value) ? value[0] : value
        if (!raw) return '-'
        const d = new Date(raw)
        if (isNaN(d.getTime())) return raw
        const dd = String(d.getDate()).padStart(2, '0')
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const yy = String(d.getFullYear())
        return `${dd}/${mm}/${yy}`
      }
    }

    // Generic date formatter for fields that sound like dates and have valid ISO strings (e.g., createdAt, updatedAt, etc.)
    if (!base.renderCell && typeof name === 'string') {
      const lowerName = name.toLowerCase()
      if (
        lowerName.includes('date') ||
        lowerName.includes('time') ||
        lowerName.endsWith('at') ||
        ['created_at', 'updated_at', 'sent_at'].includes(lowerName)
      ) {
        base.renderCell = (params: any) => {
          const val = params.value
          if (!val) return '-'
          
          if (typeof val === 'string' && val.includes('T')) {
            const d = new Date(val)
            if (!isNaN(d.getTime())) {
              const dd = String(d.getDate()).padStart(2, '0')
              const mm = String(d.getMonth() + 1).padStart(2, '0')
              const yy = String(d.getFullYear())
              const hh = String(d.getHours()).padStart(2, '0')
              const min = String(d.getMinutes()).padStart(2, '0')
              return `${dd}/${mm}/${yy} ${hh}:${min}`
            }
          }
          // Fallback for non-ISO strings or objects
          return typeof val === 'object' && val.lat != null ? `${val.lat}, ${val.lan ?? val.lng ?? ''}` : String(val)
        }
      }
    }

    // Wrap every column (except dailyUsageData chart) with CellWithCopy
    if (name !== 'dailyUsageData') {
      const original = base.renderCell
      base.renderCell = (params: any) => {
        const content = original ? original(params) : (params.formattedValue ?? params.value ?? '-')
        // A named code copies as it shows.
        const copied = namedFields.has(name)
          ? formatRowCode(params.row, params.field, params.value)
          : params.value
        return (
          <CellWithCopy value={copied}>
            {content}
          </CellWithCopy>
        )
      }
    }

    return base
  })
}
